import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  DefectLayerKind,
  DefectMeasurementSlug,
  DefectRulesetSlug,
  DefectSlug,
  DefectSurveyYearSlug,
} from 'domain/surveyDataManager/contracts';
import { Brand } from 'domain/surveyDataManager/contracts/common';
import type { ActiveLayerVisualizationSettings } from 'domain/surveyDataManager/visualization/activeLayerVisualizationSettings';
import type { RecipeFeatureFilter } from 'domain/surveyDataManager/visualization/recipeFeatureFilter';

export type ActiveDefectLayerId = Brand<string, 'ActiveDefectLayerId'>;

export type ActiveLayerInstanceId = Brand<string, 'ActiveLayerInstanceId'>;

export const DEFAULT_ACTIVE_LAYER_OPACITY = 1;
export const DEFAULT_ACTIVE_LAYER_LINE_WIDTH = 3;
export const DEFAULT_ACTIVE_LAYER_LINE_STYLE = 'solid';
export const MIN_ACTIVE_LAYER_LINE_WIDTH = 1;
export const MAX_ACTIVE_LAYER_LINE_WIDTH = 22;

export interface ActiveDefectLayer {
  readonly id: ActiveDefectLayerId;
  readonly instanceId: ActiveLayerInstanceId;
  readonly defectSlug: DefectSlug;
  readonly measurementSlug: DefectMeasurementSlug;
  readonly surveyYearSlug: DefectSurveyYearSlug;
  readonly rulesetSlug: DefectRulesetSlug;
  readonly lane: string;
  readonly recipeId: string | null;
  readonly featureFilter: RecipeFeatureFilter | null;
  readonly layerKind: DefectLayerKind;
  readonly sourceLayerId: string | null;
  readonly vectorSourceUrl: string | null;
  displayName: string;
  visible: boolean;
  opacity: number;
  highlighted: boolean;
  lineWidth: number;
  lineStyle: string;
  visualizationSettings: ActiveLayerVisualizationSettings | null;
}

export interface ActiveLayersState {
  order: ActiveDefectLayerId[];
  byId: Record<string, ActiveDefectLayer>;
}

const initialState: ActiveLayersState = {
  order: [],
  byId: {},
};

export interface AddActiveLayerInput {
  readonly defectSlug: DefectSlug;
  readonly measurementSlug: DefectMeasurementSlug;
  readonly surveyYearSlug: DefectSurveyYearSlug;
  readonly rulesetSlug: DefectRulesetSlug;
  readonly lane: string;
  readonly displayName: string;
  readonly instanceId?: ActiveLayerInstanceId;
  readonly recipeId?: string | null;
  readonly featureFilter?: RecipeFeatureFilter | null;
  readonly layerKind?: DefectLayerKind;
  readonly sourceLayerId?: string | null;
  readonly vectorSourceUrl?: string | null;
}

export function buildActiveLayerId(input: {
  readonly defectSlug: DefectSlug;
  readonly measurementSlug: DefectMeasurementSlug;
  readonly surveyYearSlug: DefectSurveyYearSlug;
  readonly rulesetSlug: DefectRulesetSlug;
  readonly lane: string;
}): ActiveDefectLayerId {
  return `${input.defectSlug}::${input.measurementSlug}::${input.surveyYearSlug}::${input.rulesetSlug}::${input.lane}` as ActiveDefectLayerId;
}

export function createActiveLayerInstanceId(): ActiveLayerInstanceId {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID() as ActiveLayerInstanceId;
  }
  return `sdm-layer-${Date.now()}-${Math.random().toString(36).slice(2, 11)}` as ActiveLayerInstanceId;
}

function toDraftFeatureFilter(
  filter: RecipeFeatureFilter | null | undefined,
): {
  bandSlugs?: string[];
  minValue?: number;
  maxValue?: number;
} | null {
  if (filter == null) {
    return null;
  }
  const draft: {
    bandSlugs?: string[];
    minValue?: number;
    maxValue?: number;
  } = {};
  if (filter.bandSlugs != null) {
    draft.bandSlugs = [...filter.bandSlugs];
  }
  if (typeof filter.minValue === 'number') {
    draft.minValue = filter.minValue;
  }
  if (typeof filter.maxValue === 'number') {
    draft.maxValue = filter.maxValue;
  }
  return draft;
}

const activeLayersSlice = createSlice({
  name: 'surveyDataManagerActiveLayers',
  initialState,
  reducers: {
    layerAdded(state, action: PayloadAction<AddActiveLayerInput>) {
      const id = buildActiveLayerId(action.payload);
      if (state.byId[id]) {
        return;
      }
      state.byId[id] = {
        id,
        instanceId: action.payload.instanceId ?? createActiveLayerInstanceId(),
        defectSlug: action.payload.defectSlug,
        measurementSlug: action.payload.measurementSlug,
        surveyYearSlug: action.payload.surveyYearSlug,
        rulesetSlug: action.payload.rulesetSlug,
        lane: action.payload.lane,
        recipeId: action.payload.recipeId ?? null,
        featureFilter: toDraftFeatureFilter(action.payload.featureFilter),
        layerKind: action.payload.layerKind ?? 'map-vector',
        sourceLayerId: action.payload.sourceLayerId?.trim() || null,
        vectorSourceUrl: action.payload.vectorSourceUrl?.trim() || null,
        displayName: action.payload.displayName,
        visible: true,
        opacity: DEFAULT_ACTIVE_LAYER_OPACITY,
        highlighted: false,
        lineWidth: DEFAULT_ACTIVE_LAYER_LINE_WIDTH,
        lineStyle: DEFAULT_ACTIVE_LAYER_LINE_STYLE,
        visualizationSettings: null,
      };
      state.order.push(id);
    },
    layerRemoved(state, action: PayloadAction<{ readonly id: ActiveDefectLayerId }>) {
      if (!state.byId[action.payload.id]) {
        return;
      }
      delete state.byId[action.payload.id];
      state.order = state.order.filter((id) => id !== action.payload.id);
    },
    layersRemovedByRecipeId(
      state,
      action: PayloadAction<{
        readonly recipeId: string;
        readonly matchingLayerIds?: readonly ActiveDefectLayerId[];
      }>,
    ) {
      const recipeId = action.payload.recipeId;
      const matchingIds = new Set(action.payload.matchingLayerIds ?? []);
      const remainingIds: ActiveDefectLayerId[] = [];
      for (const id of state.order) {
        const layer = state.byId[id];
        if (!layer) {
          continue;
        }
        if (layer.recipeId === recipeId || matchingIds.has(id)) {
          delete state.byId[id];
        } else {
          remainingIds.push(id);
        }
      }
      state.order = remainingIds;
    },
    
     // attach (or re-attach) a recipe id to an existing layer and force it visible.
     // used when a map layer already exists without recipe ownership (wizard /
     // session restore / already-active activation) so See in Map and favourites
     // stay in sync with the map.
     
    layerRecipeOwnershipAttached(
      state,
      action: PayloadAction<{
        readonly id: ActiveDefectLayerId;
        readonly recipeId: string;
      }>,
    ) {
      const layer = state.byId[action.payload.id];
      if (!layer) {
        return;
      }
      layer.recipeId = action.payload.recipeId;
      layer.visible = true;
    },
    layerVisibilityToggled(state, action: PayloadAction<{ readonly id: ActiveDefectLayerId }>) {
      const layer = state.byId[action.payload.id];
      if (!layer) {
        return;
      }
      layer.visible = !layer.visible;
    },
    layerDisplayNameUpdated(
      state,
      action: PayloadAction<{ readonly id: ActiveDefectLayerId; readonly displayName: string }>,
    ) {
      const layer = state.byId[action.payload.id];
      if (!layer) {
        return;
      }
      layer.displayName = action.payload.displayName;
    },
    layerOpacityUpdated(
      state,
      action: PayloadAction<{ readonly id: ActiveDefectLayerId; readonly opacity: number }>,
    ) {
      const layer = state.byId[action.payload.id];
      if (!layer) {
        return;
      }
      const clamped = Math.min(1, Math.max(0, action.payload.opacity));
      layer.opacity = clamped;
    },
    layerHighlightToggled(state, action: PayloadAction<{ readonly id: ActiveDefectLayerId }>) {
      const layer = state.byId[action.payload.id];
      if (!layer) {
        return;
      }
      layer.highlighted = !layer.highlighted;
    },
    layerLineWidthUpdated(
      state,
      action: PayloadAction<{ readonly id: ActiveDefectLayerId; readonly lineWidth: number }>,
    ) {
      const layer = state.byId[action.payload.id];
      if (!layer) {
        return;
      }
      const rounded = Math.round(action.payload.lineWidth);
      layer.lineWidth = Math.min(
        MAX_ACTIVE_LAYER_LINE_WIDTH,
        Math.max(MIN_ACTIVE_LAYER_LINE_WIDTH, rounded),
      );
    },
    layerLineStyleUpdated(
      state,
      action: PayloadAction<{ readonly id: ActiveDefectLayerId; readonly lineStyle: string }>,
    ) {
      const layer = state.byId[action.payload.id];
      if (!layer) {
        return;
      }
      const nextStyle = action.payload.lineStyle.trim();
      if (!nextStyle) {
        return;
      }
      layer.lineStyle = nextStyle;
    },
    layerVisualizationSettingsSaved(
      state,
      action: PayloadAction<{
        readonly id: ActiveDefectLayerId;
        readonly settings: ActiveLayerVisualizationSettings;
      }>,
    ) {
      const layer = state.byId[action.payload.id];
      if (!layer) {
        return;
      }
      const { settings } = action.payload;
      layer.visualizationSettings = {
        measurementSlug: settings.measurementSlug,
        ruleName: settings.ruleName,
        ruleDescription: settings.ruleDescription,
        colorLow: settings.colorLow,
        colorHigh: settings.colorHigh,
        thresholds: [...settings.thresholds],
        highlightValue: settings.highlightValue,
        savedAt: settings.savedAt ?? new Date().toISOString(),
      };
    },
    layerMeasurementSwitched(
      state,
      action: PayloadAction<{
        readonly id: ActiveDefectLayerId;
        readonly measurementSlug: DefectMeasurementSlug;
      }>,
    ) {
      const layer = state.byId[action.payload.id];
      if (!layer || layer.measurementSlug === action.payload.measurementSlug) {
        return;
      }

      const newId = buildActiveLayerId({
        defectSlug: layer.defectSlug,
        measurementSlug: action.payload.measurementSlug,
        surveyYearSlug: layer.surveyYearSlug,
        rulesetSlug: layer.rulesetSlug,
        lane: layer.lane,
      });

      if (state.byId[newId]) {
        return;
      }

      const orderIndex = state.order.indexOf(action.payload.id);

      delete state.byId[action.payload.id];
      state.byId[newId] = {
        id: newId,
        instanceId: layer.instanceId,
        defectSlug: layer.defectSlug,
        measurementSlug: action.payload.measurementSlug,
        surveyYearSlug: layer.surveyYearSlug,
        rulesetSlug: layer.rulesetSlug,
        lane: layer.lane,
        recipeId: layer.recipeId,
        featureFilter: toDraftFeatureFilter(layer.featureFilter),
        layerKind: layer.layerKind,
        sourceLayerId: layer.sourceLayerId,
        vectorSourceUrl: layer.vectorSourceUrl,
        displayName: layer.displayName,
        visible: layer.visible,
        opacity: layer.opacity,
        highlighted: layer.highlighted,
        lineWidth: layer.lineWidth,
        lineStyle: layer.lineStyle,
        visualizationSettings: null,
      };
      state.order = state.order.map((entryId) =>
        entryId === action.payload.id ? newId : entryId,
      );

      if (orderIndex === -1) {
        state.order.push(newId);
      }
    },
    allLayersCleared(state) {
      state.order = [];
      state.byId = {};
    },
  },
});

export const {
  layerAdded,
  layerRemoved,
  layersRemovedByRecipeId,
  layerRecipeOwnershipAttached,
  layerVisibilityToggled,
  layerDisplayNameUpdated,
  layerOpacityUpdated,
  layerHighlightToggled,
  layerLineWidthUpdated,
  layerLineStyleUpdated,
  layerVisualizationSettingsSaved,
  layerMeasurementSwitched,
  allLayersCleared,
} = activeLayersSlice.actions;

export const activeLayersReducer = activeLayersSlice.reducer;
export default activeLayersSlice.reducer;
